/**
 * Adding payout methods and requesting payouts the way Fansly's own Wallet &
 * Payouts dialogs do (bundle chunk 282, captured 2026-11-02). Every change
 * passes the step-up first (stepUpOf). Money is thousandths of a dollar.
 */
import { asList, asObj, num, str, unwrap, type Raw } from '../data'

/** Fansly's provider ids in the Add Payout Method dialog. */
export const PROVIDER = { paxum: '2', cosmo: '5', bank: '30', crypto: '151' } as const
export type ProviderTab = keyof typeof PROVIDER

/** Fansly sends field0…field10, all present, with the provider's values filled in. */
export function emptyMetadata(): Record<string, string> {
  const out: Record<string, string> = {}
  for (let i = 0; i < 11; i++) out[`field${i}`] = ''
  return out
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const validEmail = (v: string) => EMAIL.test(v.trim())

/** A Cosmo Payment SAN: 9 or 10 digits (Fansly also accepts its 00000-padded form). */
export const validSan = (v: string) => /^0{5,6}\d{4}$/.test(v) || /^\d{9,10}$/.test(v)

export function paxumBody(email: string) {
  return { providerId: PROVIDER.paxum, metadata: { ...emptyMetadata(), email: email.trim() } }
}

export function cosmoBody(san: string) {
  return { providerId: PROVIDER.cosmo, metadata: { ...emptyMetadata(), field0: san.trim() } }
}

// ── Bank: Fansly builds the form from the country (payouts/method) ─────────

export interface BankAttribute {
  type: string
  label: string
  value: string
  /** A regular expression the value must match; '' = anything. */
  validation: string
  expectedValue: string
  /** Choices for a dropdown (Fansly's inputType 2); empty for a text field. */
  options: string[]
}

/** The attribute that picks the bank (or "new bank") and reloads the form. */
const BANK_SELECT = new Set(['Fansly_BankSelect', 'Fansly_BankSelect_New'])

function attributeOf(r: Raw): BankAttribute {
  return {
    type: str(r.type),
    label: str(r.label) || str(r.name) || str(r.type),
    value: str(r.value),
    validation: str(r.validation),
    expectedValue: str(r.expectedValue),
    options: num(r.inputType) === 2 && Array.isArray(r.inputValues) ? r.inputValues.map((v) => String(v)) : [],
  }
}

export interface BankForm {
  /** Fansly's provider payout method, sent back whole with the values filled in. */
  raw: Raw
  attributes: BankAttribute[]
  bankSelect: BankAttribute | null
}

export function bankFormOf(payload: unknown): BankForm {
  const raw = asObj(unwrap(payload))
  const all = asList(raw.missingAttributes)
  const select = all.find((a) => str(a.type) === 'Fansly_BankSelect')
  return {
    raw,
    attributes: all.filter((a) => !BANK_SELECT.has(str(a.type))).map(attributeOf),
    bankSelect: select ? attributeOf(select) : null,
  }
}

export function attributeValid(a: BankAttribute, value: string): boolean {
  if (!a.validation) return true
  try {
    return new RegExp(a.validation).test(value)
  } catch {
    return true
  }
}

/** The provider method with the operator's values, for payouts/method/update. */
export function bankUpdateBody(form: BankForm, values: Record<string, string>): Record<string, unknown> {
  const attrs = asList(form.raw.missingAttributes).map((a) => {
    const type = str(a.type)
    return type in values ? { ...a, value: values[type] } : a
  })
  return { ...form.raw, missingAttributes: attrs }
}

/** After update: register it under provider 30 with its id, account number and currency. */
export function bankCreateBody(updated: unknown) {
  const r = asObj(unwrap(updated))
  let meta: Raw = {}
  try {
    meta = asObj(JSON.parse(str(r.metadata)))
  } catch {
    meta = {}
  }
  return {
    providerId: PROVIDER.bank,
    metadata: { ...emptyMetadata(), field0: str(r.id), field1: str(meta.accountNumber), field2: str(meta.currencyCode) },
  }
}

// ── Crypto ─────────────────────────────────────────────────────────────────

export interface CryptoForm {
  key: string
  name: string
  network: string
  currency: string
}

export function cryptoFormsOf(payload: unknown): { eligible: boolean; forms: CryptoForm[] } {
  const r = asObj(unwrap(payload))
  const forms = asList(r.forms).map((f) => ({ key: str(f.key), name: str(f.name) || str(f.currency), network: str(f.network), currency: str(f.currency) }))
  return { eligible: !!r.eligible && forms.length > 0, forms: forms.filter((f) => f.key) }
}

/** Invisible characters Fansly rejects in a pasted wallet address. */
const HIDDEN = /[\t-\r \xA0\u1680\u2000-\u200D\u2028\u2029\u202F\u205F\u2060\u3000\uFEFF]/
export const addressClean = (v: string) => !!v && !HIDDEN.test(v)

export function cryptoBody(form: CryptoForm, address: string, token: string) {
  return {
    providerId: PROVIDER.crypto,
    metadata: { field0: form.key, field1: address, field2: form.currency, field3: form.network, ...(token ? { field4: token } : {}) },
  }
}

// ── Step-up ("Confirm it's you") ───────────────────────────────────────────

export interface StepUp {
  token: string
  /** The code was e-mailed (else it comes from the authenticator app). */
  email: boolean
  /** Where it was e-mailed, masked by Fansly. */
  address: string
}

/** twofa/session/create: null when Fansly needs no code for this session. */
export function stepUpOf(payload: unknown): StepUp | null {
  const r = unwrap(payload)
  if (!r || typeof r !== 'object') return null
  const o = asObj(r)
  const token = str(o.token)
  if (!token) return null
  return { token, email: !!o.emailTwofa, address: str(o.email) || str(o.emailAddress) }
}

/** Removing a method answers {twofa} when Fansly wants a verification it only takes on its own site. */
export const removalNeedsVerification = (payload: unknown) => !!asObj(unwrap(payload)).twofa

// ── Payout request ─────────────────────────────────────────────────────────

/** Dollars typed by the operator → Fansly's thousandths. */
export function millsFromDollars(text: string): number {
  const n = Number(String(text).replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) * 10 : 0
}

export type RequestProblem = 'amount' | 'overBalance' | 'belowMinimum' | 'taxLocked' | null

export function requestProblem(amount: number, available: number, method: { minPayout: number; taxLocked: boolean } | null): RequestProblem {
  if (method?.taxLocked) return 'taxLocked'
  if (amount <= 0) return 'amount'
  if (amount > available) return 'overBalance'
  if (method && amount < method.minPayout) return 'belowMinimum'
  return null
}
